import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { format } from "date-fns";
import { getApiBaseUrl } from "../../services/httpServices";
import DashboardLayout from "../common/DashboardLayout";
import { certiborder, certificateImg, backgroundcertificateImg } from "../imagepath";
import "../../assets/css/certificate.css";

/**
 * Certificate of completion for a module-based custom course.
 *
 * Deliberately the same artefact as the existing certificates — same border,
 * same branding, same html2canvas/jsPDF download — because a learner should not
 * be able to tell which kind of course produced theirs. The data behind it comes
 * from the learner's own certification record, and the server refuses to build
 * one until every module has been passed.
 */
const CourseCertificate = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const certRef = useRef(null);

  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const base = getApiBaseUrl().replace(/\/+$/, "");
      const { data } = await axios.get(
        `${base}/admin/custom-training/courses/${courseId}/certificate`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!data.success) { setError(data.error || "Could not load the certificate."); return; }
      setCert(data.certificate);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load the certificate.");
    } finally { setLoading(false); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  async function download() {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, Math.min(h, pdf.internal.pageSize.getHeight()));
      pdf.save(`${(cert.title || "certificate").replace(/[^\w\- ]+/g, "")}.pdf`);
    } catch (err) {
      setError("Could not build the PDF. You can still print this page.");
    } finally { setDownloading(false); }
  }

  const fmt = (d) => (d ? format(new Date(d), "MMMM do yyyy") : "—");

  if (loading) {
    return <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp"><div className="cp-card"><p className="cp-muted">Loading certificate…</p></div></div>
    </DashboardLayout>;
  }
  if (error || !cert) {
    return <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp">
        <div className="cp-alert">{error || "Certificate not available."}</div>
        <button type="button" className="cp-btn ghost"
          onClick={() => navigate(`/custom-training/course/${courseId}`)}>Back to the course</button>
      </div>
    </DashboardLayout>;
  }

  return (
    <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp" style={{ maxWidth: 1000 }}>
        <div className="cp-top">
          <div>
            <h2>Certificate of Completion</h2>
            <div className="cp-muted">{cert.courseTitle}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="cp-btn ghost"
              onClick={() => navigate(`/custom-training/course/${courseId}`)}>Back</button>
            <button type="button" className="cp-btn" disabled={downloading} onClick={download}>
              {downloading ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </div>

        <div className="certi-div" ref={certRef}
          style={{ position: "relative", background: "#fff", padding: 0 }}>
          <img src={certiborder} alt="" style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0 }} />
          <div style={{
            position: "relative", zIndex: 1, width: "90%", minHeight: 900,
            color: "#000", textAlign: "center", padding: 20, boxSizing: "border-box", margin: "0 auto" }}>
            <img src={backgroundcertificateImg} className="certi-backimg" alt="" />

            <h1 className="certi-heading">National Certification Program</h1>
            <p className="certi-sub-heading">{cert.name}</p>
            {cert.schoolName ? <p style={{ fontSize: 28 }}>{cert.schoolName}</p> : null}
            {cert.address ? <p style={{ fontSize: 22 }}>{cert.address}</p> : null}

            <p style={{ fontSize: 24, marginTop: 5, fontWeight: 700 }}>Is Awarded &amp; Certified on</p>
            <p className="certi-date">{fmt(cert.completionDate)}</p>
            <p style={{ fontSize: 24, fontWeight: 600 }}>By the</p>
            <p style={{ fontSize: 27, fontWeight: 800, marginTop: 20 }}>
              <span className="certi-pink">Angel</span><span className="certi-blue">Watch</span> Foundation
            </p>
            <p style={{ fontSize: 21, fontWeight: 500 }}>Expiration&nbsp;{fmt(cert.expirationDate)}</p>

            <p className="align-img">
              <img src={certificateImg} className="certi-left-img" alt="" />
              <img src={certificateImg} className="certi-right-img" alt="" />
            </p>

            {cert.stateid ? (
              <h2 style={{ fontSize: 16, marginBottom: 2, fontWeight: 500 }}>Lic.{cert.stateid}</h2>
            ) : null}
            <h2 style={{ fontSize: 30, marginTop: 30, color: "red", fontWeight: 700, fontFamily: "Tahoma" }}>
              {cert.title}
            </h2>
            {cert.hours ? (
              <p style={{ fontSize: 18, fontWeight: 600 }}>{cert.hours} training hours</p>
            ) : null}

            <p style={{ fontSize: 24, marginTop: 16, fontWeight: 600 }}>
              The <span className="certi-pink">Angel</span><span className="certi-blue">Watch</span> National
              Accreditation Program for the Safety &amp; Security of Children
            </p>
            <p className="left-text">www.AngelWatch.org</p>
            <p className="right-text">Phone: 1-800-381-6794</p>
          </div>
        </div>

        {/* The per-module record, alongside rather than on the certificate — the
            printed course sheet carries the same table.

            Omitted entirely for a PDF-only course, which is read and then passed
            in one quiz and so has no modules to score. Rendering the heading
            over an empty list would look like a certificate with something
            missing rather than one of a different shape. */}
        {(cert.modules || []).length > 0 && (
          <div className="cp-card" style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 16 }}>Module results</h3>
            <div className="cp-modules">
              {cert.modules.map((m) => (
                <div key={m.moduleNumber} className="cp-mod">
                  <b>{m.isFinal ? "Final" : `Module ${m.moduleNumber}`}</b>
                  <span>{m.title}</span>
                  <em>{m.correctCount != null ? `${m.correctCount}/${m.total} · ${m.score}%` : "—"}</em>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CourseCertificate;
